/**
 * Facts about the studio that appear in more than one place.
 *
 * Anything a lawyer, a registrar or a client would care about lives here rather
 * than being typed into each page, so there is exactly one line to change when
 * the address, the contact or a rate changes.
 */

/*
 * The address that actually serves the site, and therefore what canonical tags,
 * hreflang and Open Graph URLs must point at.
 *
 * www, not the bare domain, because buildpassing.com redirects to it — the same
 * convention as the three product sites. A canonical URL that redirects wastes
 * a hop and muddies the signal.
 */
import { SITE_ORIGIN } from '../../shared/seo';

export { SITE_ORIGIN };

export const COMPANY = {
  /**
   * The name that answers for the studio in law.
   *
   * This does NOT have to be a registered company. Operating as an individual
   * is fine — a sole trader is a perfectly valid data controller. Before
   * signing a client contract this should be the exact name that will appear on
   * it. See docs/legal.html.
   */
  legalName: 'BuildPassing',

  /** The name shown in ordinary copy — the site, the emails, the marketing. */
  tradeName: 'Build Passing',

  /**
   * Postal address of whoever is responsible for the data.
   *
   * Deliberately EMPTY until there is a real one. The legal pages check for
   * that and fall back to the email contact rather than printing a placeholder
   * — a made-up address in a privacy notice is worse than no address, because
   * it looks like a considered answer.
   */
  address: '',

  /** Where the studio is established. Used for the governing-law clause. */
  country: 'Mexico',

  founded: '2026',

  domain: 'buildpassing.com',
  url: SITE_ORIGIN,
  email: 'hola@buildpassing.com',
  privacyEmail: 'hola@buildpassing.com',
  supportEmail: 'hola@buildpassing.com',

  github: 'https://github.com/BuildPassing',
  linkedin: 'https://www.linkedin.com/company/buildpassing',
} as const;

/**
 * The products the studio funds and ships itself.
 *
 * They are on the page as evidence, not as a portfolio: the argument is that a
 * studio which maintains its own software in the stores runs a release process
 * it cannot walk away from. Keep the status honest — a claim that is generous
 * today becomes a lie in a month, and this is the section a prospective client
 * will actually click through and check.
 *
 * `slug` builds the screenshot path, `/products/<slug>-<locale>.png`. Those are
 * captured from the live sites rather than taken from the brand kit: the kit's
 * were shot before the rebrand and before this month's fixes, so its Two Milks
 * image still showed the retired name. A screenshot of your own work has to be
 * of the work as it currently stands, or it is just a nicer-looking lie.
 */
/**
 * `stack` reads framework · on-device store · backend, and is taken from each
 * product's own repository, not from memory. All three are local-first apps
 * that sync through Supabase: the Flutter ones keep a Drift/SQLite database on
 * the phone, TwoCoins keeps its ledger in IndexedDB and ships as a PWA. Where
 * they are hosted (Render) and how they send mail (Resend) is shared with this
 * site and is not what a card is for. Verified against the repos 2026-09-15 —
 * the previous lines named Node and Django, and no product uses either.
 *
 * `tone` colours the status pill: `live` is the kit's green and is reserved
 * for shipped, `next` is a warm amber for the one about to ship, `quiet` is
 * neutral for anything still being built. See tokens.css.
 */
export const PRODUCTS = [
  {
    name: 'Rickey',
    slug: 'rickey',
    domain: 'rickey.app',
    url: 'https://www.rickey.app',
    stack: 'flutter · sqlite · supabase',
    tone: 'live',
  },
  {
    name: 'TwoCoins',
    slug: 'twocoins',
    domain: 'twocoins.app',
    url: 'https://www.twocoins.app',
    stack: 'react · typescript · indexeddb · supabase',
    tone: 'quiet',
  },
  {
    name: 'Two Milks',
    slug: 'twomilks',
    domain: 'twomilks.app',
    url: 'https://www.twomilks.app',
    stack: 'flutter · sqlite · supabase realtime',
    tone: 'next',
  },
] as const;

/**
 * Published rates.
 *
 * Here rather than in the dictionaries because the two languages quote
 * different numbers, not translations of one number: a Mexican client is quoted
 * a Mexican rate, not a converted US one. Changing a price means changing it
 * here and nowhere else.
 */
export const RATES = {
  en: {
    discovery: '$2,000',
    project: 'From $8,000',
    retainer: 'From $3,200 / month',
    embedded: '$520 / day',
  },
  es: {
    discovery: '$35,000 MXN',
    project: 'Desde $90,000 MXN',
    retainer: 'Desde $25,000 MXN / mes',
    embedded: '$6,000 MXN / día',
  },
} as const;

/**
 * The founding-client discount.
 *
 * Flip to false once the three engagements are booked. The section, the hero's
 * mention of it and the note in the form all disappear together, so there is no
 * stale offer left advertised in a corner of the page.
 */
export const LAUNCH_OFFER_OPEN = true;

/** True once a real postal address has been filled in above. */
export const hasPostalAddress = (): boolean => COMPANY.address.trim().length > 0;

/**
 * The date the current legal texts took effect. Update it whenever a legal page
 * changes materially — the privacy notice promises we will.
 */
/**
 * Shown on both legal pages. Bump it whenever their text changes in substance.
 *
 * Last change: the Supabase project was deleted, which removed three things the
 * texts described — the stored copy of each proposal request, the cookieless
 * page-view counter, and the record of how a sent proposal was read. The
 * proposal form now does what it did originally and nothing more: it sends an
 * email. The flat "no analytics" claim is true again, so the wording the entry
 * below was written to qualify has gone back to being accurate.
 *
 * Previously: the Cookie Policy's analytics claim. It said "we do not know how
 * many people visit this site", which stopped being wholly true when the site
 * began counting its own page views — cookieless, with no identifier and no IP
 * stored, so the claim about *people* survives but the flat "no analytics" did
 * not.
 */
export const LEGAL_LAST_UPDATED = '2026-09-08';
